import { Link } from 'react-router-dom';
import { PageHeader, Panel, StatTile } from '../components/Page';
import { CardSkeleton, EmptyState, ErrorState, Skeleton, TableSkeleton } from '../components/Feedback';
import { VolumeChart } from '../components/VolumeChart';
import {
  ConfidenceBadge,
  DirectionChip,
  EntityTypeChip,
  HealthDot,
  OutcomeChip,
  StatusChip,
} from '../components/Chips';
import { TableWrap } from '../components/Table';
import { OnboardingSummary } from '../components/Onboarding';
import { dashboard } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import {
  formatDateTime,
  formatDuration,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  formatPercent,
  formatRelative,
} from '../lib/format';
import { QboConnectionStatus, QboConnectionStatusLabels } from '../lib/enums';
import type { QboConnectionDto } from '../lib/types';

function ConnectionBanner({ connection }: { connection: QboConnectionDto }): JSX.Element {
  const { status } = connection;
  const tone =
    status === QboConnectionStatus.Connected
      ? 'good'
      : status === QboConnectionStatus.ExpiringSoon
        ? 'warn'
        : status === QboConnectionStatus.NotConnected
          ? 'neutral'
          : 'bad';

  const headline =
    status === QboConnectionStatus.Connected
      ? `Connected to ${connection.companyName ?? 'QuickBooks Online'}`
      : status === QboConnectionStatus.ExpiringSoon
        ? 'QuickBooks re-consent required soon'
        : status === QboConnectionStatus.NotConnected
          ? 'QuickBooks Online is not connected'
          : `QuickBooks connection ${QboConnectionStatusLabels[status].toLowerCase()}`;

  return (
    <div className={`banner banner-${tone}`} role={tone === 'bad' ? 'alert' : undefined}>
      <div className="banner-body">
        <p className="banner-title">
          {headline}
          {connection.isSandbox ? <span className="banner-tag">Sandbox</span> : null}
        </p>
        <p className="banner-detail">
          {/* The company name is in the headline only when connected, so repeat it here. */}
          {connection.companyName ? `${connection.companyName} · ` : ''}
          {connection.realmId ? `Realm ${connection.realmId}` : 'No realm linked'}
          {connection.homeCurrencyCode ? ` · ${connection.homeCurrencyCode}` : ''}
          {connection.daysUntilReconsentRequired !== null
            ? ` · Re-consent in ${connection.daysUntilReconsentRequired} days`
            : ''}
          {connection.lastSuccessfulCallUtc
            ? ` · Last call ${formatRelative(connection.lastSuccessfulCallUtc)}`
            : ''}
        </p>
        {connection.lastError ? <p className="banner-error">{connection.lastError}</p> : null}
      </div>
      <Link className="btn btn-secondary btn-sm" to="/settings">
        Manage connection
      </Link>
    </div>
  );
}

export function DashboardPage(): JSX.Element {
  const state = useAsync((signal) => dashboard.get(signal), []);

  if (state.loading && !state.data) {
    return (
      <>
        <PageHeader title="Dashboard" description="Gateway throughput and QuickBooks health." />
        <Skeleton height={64} radius={10} />
        <div style={{ height: 16 }} />
        <CardSkeleton count={8} />
        <div style={{ height: 16 }} />
        <TableSkeleton rows={5} columns={4} />
      </>
    );
  }

  if (state.error && !state.data) {
    return (
      <>
        <PageHeader title="Dashboard" />
        {/* Setup state still matters — especially when the dashboard itself is failing. */}
        <OnboardingSummary />
        <ErrorState message="The dashboard could not be loaded." detail={state.error} onRetry={state.reload} />
      </>
    );
  }

  if (!state.data) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <OnboardingSummary />
        <EmptyState title="No dashboard data" description="The gateway returned an empty response." />
      </>
    );
  }

  const { connection, counts, recentActivity, recentDocuments, volume, botHealth } = state.data;
  const currency = connection.homeCurrencyCode;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="Gateway throughput and QuickBooks health."
        actions={
          <button type="button" className="btn btn-secondary" onClick={state.reload} disabled={state.loading}>
            {state.loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      <OnboardingSummary />

      <ConnectionBanner connection={connection} />

      <div className="stat-grid">
        <StatTile
          label="Pending approvals"
          value={formatNumber(counts.pendingApprovals)}
          tone={counts.pendingApprovals > 0 ? 'warn' : 'neutral'}
          footer={<Link to="/approvals">Open queue</Link>}
        />
        <StatTile
          label="Awaiting review"
          value={formatNumber(counts.awaitingReview)}
          tone={counts.awaitingReview > 0 ? 'info' : 'neutral'}
          footer={<Link to="/purchase-invoices">Review now</Link>}
        />
        <StatTile label="Processing" value={formatNumber(counts.processingNow)} hint="With the AI bot" />
        <StatTile label="Posted today" value={formatNumber(counts.postedToday)} tone="good" />
        <StatTile
          label="Failed today"
          value={formatNumber(counts.failedToday)}
          tone={counts.failedToday > 0 ? 'bad' : 'neutral'}
          footer={<Link to="/history">Inspect history</Link>}
        />
        <StatTile
          label="Posted this month"
          value={formatNumber(counts.postedThisMonth)}
          hint={`${formatMoneyCompact(counts.postedValueThisMonth, currency)} posted value`}
        />
        <StatTile
          label="Average confidence"
          value={formatPercent(counts.averageConfidenceThisMonth, 1)}
          hint="This month"
        />
        <StatTile
          label="Post success rate"
          value={`${counts.postSuccessRatePercent.toFixed(1)}%`}
          tone={counts.postSuccessRatePercent >= 95 ? 'good' : counts.postSuccessRatePercent >= 80 ? 'warn' : 'bad'}
        />
      </div>

      <div className="grid-2">
        <Panel title="Volume" description="Documents received, posted and failed by day.">
          <VolumeChart data={volume} />
        </Panel>

        <Panel title="iERP AI Bot health">
          <div className="health-card">
            <div className="health-row">
              <HealthDot ok={botHealth.reachable} title={botHealth.reachable ? 'Reachable' : 'Unreachable'} />
              <span className="health-status">{botHealth.reachable ? 'Reachable' : 'Unreachable'}</span>
              {botHealth.latencyMs !== null ? (
                <span className="health-latency">{formatDuration(botHealth.latencyMs)}</span>
              ) : null}
            </div>
            <dl className="kv-list kv-cols-1">
              <div className="kv">
                <dt className="kv-label">Endpoint</dt>
                <dd className="kv-value mono">{botHealth.baseUrl}</dd>
              </div>
              <div className="kv">
                <dt className="kv-label">Version</dt>
                <dd className="kv-value">{botHealth.version ?? '—'}</dd>
              </div>
              <div className="kv">
                <dt className="kv-label">Checked</dt>
                <dd className="kv-value">{formatDateTime(botHealth.checkedUtc)}</dd>
              </div>
            </dl>
            {botHealth.message ? <p className="health-message">{botHealth.message}</p> : null}
          </div>
        </Panel>
      </div>

      <div className="grid-2">
        <Panel title="Recent documents" actions={<Link to="/documents">View all</Link>}>
          {recentDocuments.length === 0 ? (
            <EmptyState title="Nothing uploaded yet" description="Drop an invoice on the Documents page." />
          ) : (
            <TableWrap>
              <thead>
                <tr>
                  <th scope="col">File</th>
                  <th scope="col">Vendor</th>
                  <th scope="col" className="num">
                    Total
                  </th>
                  <th scope="col">Confidence</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentDocuments.map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <Link to={`/purchase-invoices?document=${doc.id}`} className="cell-link">
                        {doc.fileName}
                      </Link>
                      <span className="cell-sub">{formatRelative(doc.createdUtc)}</span>
                    </td>
                    <td>{doc.vendorName ?? '—'}</td>
                    <td className="num">{formatMoney(doc.total, doc.currencyCode)}</td>
                    <td>
                      <ConfidenceBadge value={doc.confidence} size="sm" />
                    </td>
                    <td>
                      <StatusChip status={doc.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Panel>

        <Panel title="Recent activity" actions={<Link to="/history">Full history</Link>}>
          {recentActivity.length === 0 ? (
            <EmptyState title="No sync activity" description="Pull or post something to populate the feed." />
          ) : (
            <ul className="activity-feed">
              {recentActivity.map((entry) => (
                <li className="activity-item" key={entry.id}>
                  <div className="activity-head">
                    <OutcomeChip outcome={entry.outcome} />
                    <DirectionChip direction={entry.direction} />
                    <EntityTypeChip entityType={entry.entityType} />
                    <span className="activity-time">{formatRelative(entry.createdUtc)}</span>
                  </div>
                  <p className="activity-operation">
                    <span className="mono">{entry.operation}</span>
                    {entry.fileName ? <span className="activity-file"> · {entry.fileName}</span> : null}
                  </p>
                  {entry.message ? <p className="activity-message">{entry.message}</p> : null}
                  <p className="activity-meta">
                    {entry.performedByEmail ?? 'system'} · {formatDuration(entry.durationMs)}
                    {entry.httpStatusCode !== null ? ` · HTTP ${entry.httpStatusCode}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
